import { Field, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, FormItem, Stack, TextInput } from '@carbon/react';
import DndUploader from 'src/components/Uploader/DNDUploader';
import { authClient } from 'src/services/auth/client';
import StepLayout from './StepLayout';
import { profileSchema } from './schemas';

const ProfileStep = ({ onDone, onSkip }: { onDone: () => void; onSkip: () => void }) => {
  const { t } = useTranslation('authentication');
  const [image, setImage] = useState('');
  const [error, setError] = useState<Error | null>(null);

  async function onSubmit(values: { firstName: string; lastName: string }) {
    const name = `${values.firstName} ${values.lastName}`.replace(/\s+/g, ' ').trim();
    try {
      const { error: updateError } = await authClient.updateUser({ name, image: image || undefined });
      if (updateError) throw new Error(updateError.message);
      onDone();
    } catch (e) {
      setError(e as Error);
    }
  }

  return (
    <Formik
      initialValues={{ firstName: '', lastName: '' }}
      validationSchema={toFormikValidationSchema(profileSchema(t))}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, isSubmitting, isValid, dirty }) => (
        <StepLayout
          title={t('onboarding.profile.title')}
          subtitle={t('onboarding.profile.subtitle')}
          help={t('onboarding.profile.help')}
          error={error}
          onSubmit={handleSubmit}
          actions={
            <>
              <Button type="submit" disabled={isSubmitting || !isValid || !dirty}>
                {t('onboarding.continue')}
              </Button>
              <Button kind="ghost" type="button" onClick={onSkip}>
                {t('onboarding.skip')}
              </Button>
            </>
          }
        >
          <Stack gap={5}>
            <FormItem>
              <p className="cds--file--label">{t('onboarding.profile.avatar')}</p>
              <DndUploader
                useCropper
                initialValue={null}
                cropperAspectRatio={1}
                previewAspectRatio="1x1"
                onChange={({ fileId }) => setImage(fileId)}
              />
            </FormItem>
            <Field name="firstName">
              {({ field, meta }) => (
                <TextInput
                  id="profile-first-name"
                  labelText={t('onboarding.profile.firstName')}
                  invalid={!!(meta.touched && meta.error)}
                  invalidText={meta.error}
                  {...field}
                />
              )}
            </Field>
            <Field name="lastName">
              {({ field, meta }) => (
                <TextInput
                  id="profile-last-name"
                  labelText={t('onboarding.profile.lastName')}
                  invalid={!!(meta.touched && meta.error)}
                  invalidText={meta.error}
                  {...field}
                />
              )}
            </Field>
          </Stack>
        </StepLayout>
      )}
    </Formik>
  );
};

export default ProfileStep;
